// Pure helpers for the Quick Bid flow.
//
// Kept free of Vue and axios so the payload contract can be unit tested.
// The payload shape here must stay equivalent to the one the existing
// configurator sends (Product.vue:596-620) — the backend accepts one shape.

export function toInt(value) {
  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) ? 0 : parsed;
}

export function isLuxury(car) {
  return Boolean(car?.company?.noPrice || car?.modelSeries?.noPrice);
}

export function paidOptions(options) {
  return (options || []).filter((option) => toInt(option?.price) > 0);
}

export function calcMsrp({ car, color, options, deliveryFee }) {
  const optionsTotal = paidOptions(options).reduce(
    (total, option) => total + toInt(option.price),
    0
  );
  return toInt(car?.price) + toInt(color?.price) + optionsTotal + toInt(deliveryFee);
}

// UX-01: dealers rarely accept an offer more than this far below MSRP.
export const UNLIKELY_PERCENT = 25;

// UX-02: at most this many offers per user per rolling 24 hours (backend is the
// authority; this lets the UI say so before the request is sent).
export const MAX_OFFERS_PER_DAY = 2;
export const OFFER_LIMIT_MESSAGE =
  "You can send up to 2 offers every 24 hours. Please try again later.";
const DAY_MS = 24 * 60 * 60 * 1000;

function offerLogKey(userId) {
  return `qbOfferLog:${userId || "anon"}`;
}

export function recentOfferTimes(userId, now = Date.now()) {
  try {
    const list = JSON.parse(localStorage.getItem(offerLogKey(userId)) || "[]");
    return (Array.isArray(list) ? list : []).filter((t) => now - t < DAY_MS);
  } catch (e) {
    return [];
  }
}

export function offerLimitReached(userId, now = Date.now()) {
  return recentOfferTimes(userId, now).length >= MAX_OFFERS_PER_DAY;
}

export function recordOffer(userId, now = Date.now()) {
  try {
    localStorage.setItem(
      offerLogKey(userId),
      JSON.stringify([...recentOfferTimes(userId, now), now])
    );
  } catch (e) {
    // private mode: the server limit still applies
  }
}

export function belowMsrp(msrp, target) {
  const msrpValue = toInt(msrp);
  const targetValue = toInt(target);
  if (msrpValue <= 0 || targetValue <= 0) return { amount: 0, percent: 0 };
  const amount = msrpValue - targetValue;
  return { amount, percent: Math.round((amount / msrpValue) * 1000) / 10 };
}

export function buildBidPayload({
  car,
  color,
  options,
  targetPrice,
  user,
  deliveryFee,
  comments,
  discountPercentage,
}) {
  const variants = paidOptions(options).map((option) => option._id);
  if (color?._id) variants.push(color._id);

  // Luxury (noPrice) brands name a percentage under MSRP instead of a dollar
  // amount (DATA-02); the shape matches Product.vue's luxury path.
  const luxury = isLuxury(car);
  const payload = {
    carMaker: car?.company?.name,
    carName: car?.name,
    carImage: color?.url?.[0] || car?.imageUrl,
    carMsrp: calcMsrp({ car, color, options, deliveryFee }).toString(),
    userComments: comments || "",
    carColor: color?.typeSubCategory,
    userName: user?.fullName,
    userEmail: user?.email,
    userId: user?._id,
    carId: car?._id,
    variants,
    deliveryFee: toInt(deliveryFee).toString(),
    carBid: luxury ? "0" : targetPrice,
  };
  if (luxury) {
    payload.noPrice = true;
    payload.discountPercentage = parseFloat(discountPercentage);
  }
  return payload;
}
